import { useRef, useState } from "react";
import { motion, useMotionValueEvent, useScroll, useTransform } from "framer-motion";
import { PageHero } from "@/components/site/PageHero";
import { FadeUp, MaskLines, Em, Eyebrow } from "@/components/site/Motion";
import { MagneticButton } from "@/components/site/Magnetic";
import { HeroCard } from "@/components/site/HeroCard";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const STEPS = [
  { t: "00:00", h: "the brief lands", b: "Form, email or phone. However it arrives, it's logged and the clock starts. No queue, no ticket number." },
  { t: "00:10 – 01:00", h: "calibration call", b: "A recruiter who's sat on the technical side calls you. Must-haves, nice-to-haves, the stuff that never makes it into a JD." },
  { t: "01:00 – 03:00", h: "work the bench", b: "We go to people we already know first. Then referrals. Then the quiet ones who aren't 'looking' but would move for the right thing." },
  { t: "03:00 – 04:30", h: "screen like it's ours", b: "A real conversation about the work, plus the boring essentials: availability, notice, rate, location, right to work." },
  { t: "≤ 05:00", h: "first shortlist", b: "Names on your desk with a short, honest note on each. Why them. What to probe. What they want." },
  { t: "after", h: "interviews → offer → day one", b: "We run logistics, keep both sides warm, and check in at 30, 60 and 90 days. The play isn't over at the signature." },
];

const Clock = ({ progress }) => {
  const [i, setI] = useState(0);
  useMotionValueEvent(progress, "change", (v) => setI(Math.min(STEPS.length - 1, Math.floor(v * STEPS.length))));
  const deg = useTransform(progress, [0, 1], [0, 360]);
  return (
    <div className="relative mx-auto aspect-square w-56 sm:w-72" data-testid="timeline-clock">
      <svg viewBox="0 0 100 100" className="h-full w-full -rotate-90">
        <circle cx="50" cy="50" r="44" fill="none" stroke="#0E0E10" strokeOpacity=".15" strokeWidth="7" />
        <motion.circle cx="50" cy="50" r="44" fill="none" stroke="#FF4D1A" strokeWidth="7" style={{ pathLength: progress }} />
      </svg>
      <motion.div style={{ rotate: deg }} className="absolute inset-0"><span className="absolute left-1/2 top-[4%] h-3 w-3 -translate-x-1/2 rounded-full bg-[#0E0E10]" /></motion.div>
      <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
        <span className="font-mono-x text-xs uppercase tracking-[0.2em] text-[#57534B]">step 0{i + 1}</span>
        <span className="font-mono-x mt-1 px-6 text-xl font-bold sm:text-2xl">{STEPS[i].t}</span>
      </div>
    </div>
  );
};

const Timeline = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 0.6", "end 0.6"] });
  return (
    <section ref={ref} className="px-5 py-24 sm:px-8 lg:py-32" data-testid="timeline-section">
      <div className="mx-auto grid max-w-[1400px] gap-14 lg:grid-cols-12">
        <div className="lg:col-span-5"><div className="lg:sticky lg:top-32"><Clock progress={scrollYProgress} /></div></div>
        <ol className="lg:col-span-7">
          {STEPS.map((s, i) => (
            <FadeUp key={s.t} className="border-t-2 border-[#0E0E10] py-10" data-testid={`timeline-step-${i}`}>
              <span className={`font-mono-x inline-block rounded-full px-3 py-1.5 text-sm font-bold ${i === 4 ? "bg-[#FF4D1A] text-white" : "bg-[#0E0E10] text-[#F1ECE2]"}`}>{s.t}</span>
              <h3 className="font-display mt-5 text-3xl font-bold tracking-tight sm:text-4xl">{s.h}</h3>
              <p className="mt-3 max-w-lg leading-relaxed text-[#3d3a35]">{s.b}</p>
            </FadeUp>
          ))}
        </ol>
      </div>
    </section>
  );
};

const CHECKS = ["can they do the work, not just talk about it", "do they actually want this role", "can they start when you need them", "will your team be glad they came", "is the paperwork clean"];

const Checks = () => (
  <section className="bg-[#D4FF3D] px-5 py-24 sm:px-8 lg:py-32" data-testid="checks-section">
    <div className="mx-auto max-w-[1400px]">
      <Eyebrow>what we screen for</Eyebrow>
      <ul className="mt-10">
        {CHECKS.map((c, i) => (
          <FadeUp key={c} delay={i * 0.05} className="group flex items-baseline gap-6 border-t-2 border-[#0E0E10] py-5">
            <span className="font-mono-x text-sm">0{i + 1}</span>
            <span className="font-display text-2xl font-bold tracking-tight transition-transform duration-500 group-hover:translate-x-2 sm:text-4xl">{c}</span>
          </FadeUp>
        ))}
      </ul>
    </div>
  </section>
);

const FAQ = [
  ["Is five hours a promise for every role?", "For most roles, yes: five working hours to a first shortlist. For very niche or senior roles we'll tell you on the first call how long it will honestly take, and why. We'd rather be straight than fast and wrong."],
  ["Which regions do you hire for?", "Teams in the USA are looked after by PITSUS. Teams in the UK, across EMEA and through APAC are looked after by PITSIND. Same playbook, same clock."],
  ["Can we work with both wings at once?", "Yes. Plenty of roles need a US-facing lead and a delivery team in India. One brief, one point of contact, both wings on it."],
  ["How do commercials work?", "It depends on the model: contract, contract-to-hire, permanent or a dedicated pod. We'll walk you through it on the first call, in plain numbers."],
];

const Faq = () => (
  <section className="px-5 py-24 sm:px-8 lg:py-32" data-testid="faq-section">
    <div className="mx-auto grid max-w-[1400px] gap-12 lg:grid-cols-12">
      <MaskLines as="h2" lines={["fair", <Em>questions.</Em>]} className="font-display text-5xl font-bold leading-[0.95] tracking-[-0.03em] sm:text-6xl lg:col-span-4" />
      <Accordion type="single" collapsible className="lg:col-span-8">
        {FAQ.map(([q, a], i) => (
          <AccordionItem key={q} value={`q${i}`} className="border-b-2 border-[#0E0E10]" data-testid={`faq-item-${i}`}>
            <AccordionTrigger className="font-display py-6 text-left text-xl font-bold hover:no-underline sm:text-2xl" data-testid={`faq-trigger-${i}`}>{q}</AccordionTrigger>
            <AccordionContent className="pb-6 text-base leading-relaxed text-[#3d3a35]">{a}</AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  </section>
);

export default function HowWeWork() {
  return (
    <div data-testid="how-page">
      <PageHero testid="how-hero" eyebrow="the play, hour by hour" lines={["from your brief", "to real names", <>in <Em className="text-[#FF4D1A]">five hours.</Em></>]}
        aside={<HeroCard big="6" unit="steps" tone="acid" caption="Brief, call, bench, screen, shortlist, day one. Each one timed, each one yours to hold us to." testid="how-hero-card" />}
        lead="Here is exactly what happens after you hit send, and who is doing it.">
        <div className="mt-10"><MagneticButton to="/clients" tone="ink" testid="how-brief-btn">start a brief</MagneticButton></div>
      </PageHero>
      <Timeline />
      <Checks />
      <Faq />
    </div>
  );
}
